import { dateTimeFormatter, hockeyFormatter, statFormatter, stringFormatter } from '@nhl/formatter-utilities';

import GlobalColumnFunctions from '../../../../../../../utils/globalColumnFunctions';
import BaseColumnDefinitions from '../baseColumnDefinitions';

import { skaterInternationalColumns } from './skaterInternationalColumns';
import { skaterMultiPerformancesColumnDefinitions } from './skaterMultiPerformancesColumnDefinitions';

// Shortening the function name
const CreateHeaderCell = BaseColumnDefinitions.CreateHeaderCell;

// Columns -- listed here alphabetically
const GAME_WINNING_GOAL = {
    accessor: 'gameWinningGoals',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Game Winning Goals', 'GWG'),
};
const OVERTIME_GOALS_COL = {
    accessor: 'overtimeGoals',
    Header: CreateHeaderCell('Overtime Goals', 'OT G'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const OVERTIME_ASSISTS_COL = {
    accessor: 'overtimeAssists',
    Header: CreateHeaderCell('Overtime Assists', 'OT A'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const OVERTIME_POINTS_COL = {
    accessor: 'overtimePoints',
    Header: CreateHeaderCell('Overtime Points', 'OT P'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const GAME_SEVEN_GOALS_COL = {
    accessor: 'gameSevenGoals',
    Header: CreateHeaderCell('Game Seven Goals', 'G7 G'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const GAME_SEVEN_ASSISTS_COL = {
    accessor: 'gameSevenAssists',
    Header: CreateHeaderCell('Game Seven Assists', 'G7 A'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const GAME_SEVEN_POINTS_COL = {
    accessor: 'gameSevenPoints',
    Header: CreateHeaderCell('Game Seven Points', 'G7 P'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const GAME_SEVEN_GAMES_PLAYED_COL = {
    accessor: 'gameSevenGamesPlayed',
    Header: CreateHeaderCell('Game Seven Games Played', 'G7 GP'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const GAME_SEVEN_WINS_COL = {
    accessor: 'gameSevenWins',
    Header: CreateHeaderCell('Game Seven Wins', 'G7 W'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const GAME_SEVEN_LOSSES_COL = {
    accessor: 'gameSevenLosses',
    Header: CreateHeaderCell('Game Seven Losses', 'G7 L'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const GAME_SEVEN_WIN_PERCENTAGE_COL = {
    accessor: 'gameSevenWinPercentage',
    Header: CreateHeaderCell('Game Seven Win Percentage', 'G7 W%'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: (cellData) => {
        const games = cellData.row.values.gameSevenGamesPlayed;
        const wins = cellData.row.values.gameSevenWins;
        return statFormatter.displayValueAs('pointPctg')(wins / games);
    },
};
const GAME_SEVEN_RECORD = {
    accessor: 'gameSevenRecord',
    Header: CreateHeaderCell('Game Seven Record', 'G7 Record'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: (cellData) => {
        const wins = cellData.row.original.gameSevenWins || 0;
        const losses = cellData.row.original.gameSevenLosses || 0;
        return stringFormatter.cellDisplay(wins + ' - ' + losses);
    },
    disableSortBy: true,
};
const POINT_STREAK = {
    accessor: 'pointStreak',
    Header: CreateHeaderCell('Point Streak'),
    getProps: GlobalColumnFunctions.getProps,
};
const PLAYOFFS_SEASON_GP = {
    accessor: 'poGamesPlayed',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Playoffs Games Played', 'PO GP'),
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const POSITION_CODE_COL = {
    accessor: 'positionCode',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Position', 'Pos'),
};
const REGULAR_SEASON_GP = {
    accessor: 'rsGamesPlayed',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Regular Season Games Played', 'RS GP'),
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const SECONDS_OF_PERIOD_COL = {
    accessor: 'secondsOfPeriod',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Seconds of Period', 'Time'),
    Cell: ({ value }) => dateTimeFormatter.displaySecondsFormatted(value),
};
const SECONDS_SINCE_LAST_PLAYER_GAME_GOAL_COL = {
    accessor: 'secondsSinceLastPlayerGameGoal',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Seconds Since Last Goal', 'Time'),
    Cell: ({ value }) => dateTimeFormatter.displaySecondsFormatted(value),
};
const SECONDS_FROM_PERIOD_START_COL = {
    accessor: 'secondsSinceStartOfPeriod',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Seconds Since Start Of Period', 'Time'),
    Cell: ({ value }) => dateTimeFormatter.displaySecondsFormatted(value),
};
const SECONDS_FROM_LAST_2_GOALS_COL = {
    accessor: 'secondsSinceLast2Goals',
    Header: CreateHeaderCell('Seconds Since Last Two Goals', 'Time'),
    Cell: ({ value }) => dateTimeFormatter.displaySecondsFormatted(value),
    getProps: GlobalColumnFunctions.getProps,
};
const SHIFTS_COL = {
    accessor: 'shifts',
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
    Header: CreateHeaderCell('Shifts'),
};
const STANLEY_CUP_WINS = {
    accessor: 'stanleyCupWins',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Stanley Cup Wins', 'Cup Wins'),
};
const THREE_GOAL_GAMES_COL = {
    accessor: 'threeGoalGames',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Three Goal Games'),
};
const FOUR_GOAL_GAMES_COL = {
    accessor: 'fourGoalGames',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Four Goal Games'),
};
const FIVE_GOAL_GAMES_COL = {
    accessor: 'fiveGoalGames',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Five Goal Games'),
};
const THREE_OR_MORE_GOAL_GAMES_COL = {
    accessor: 'threeOrMoreGoalGames',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Three Or More Goal Games'),
};
const PLUS_MINUS_COL = {
    accessor: 'plusMinus',
    Header: CreateHeaderCell('+/-'),
    getProps: GlobalColumnFunctions.getProps,
    Cell: ({ value }) => stringFormatter.cellDisplay(value),
};
const SHOOTING_PCTG_COL = {
    accessor: 'shootingPctg',
    Header: CreateHeaderCell('Shooting %', 'S%'),
    Cell: ({ value }) => statFormatter.displayValueAs('shootingPctg')(value, true),
    getProps: GlobalColumnFunctions.getProps,
    sortType: 'basic',
};
const WIN_LOSS_TIE_RECORD_COL = {
    accessor: 'wins',
    Header: 'Record (W-L-T)',
    getProps: GlobalColumnFunctions.getProps,
    Cell: (cellData) => {
        const wins = cellData.row.original.wins;
        const losses = cellData.row.original.losses;
        const ties = cellData.row.original.ties;
        return stringFormatter.cellDisplay(wins + ' - ' + losses + ' - ' + ties);
    },
    disableSortBy: true,
};
const HAT_TRICKS_COL = {
    accessor: 'hatTricks',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Hat Tricks'),
};

const PLAYER_AGE_COL = {
    accessor: 'ageInDays',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Age'),
    Cell: ({ original }) => original.ageInYears + ' years, ' + original.ageInDaysForYear + ' days',
};

const TIME_OF_GOAL_COL = {
    accessor: 'timeOfGoal',
    getProps: GlobalColumnFunctions.getProps,
    Header: CreateHeaderCell('Time of Goal'),
};

// Column Groupings
const ELIMINATION_GAME_CAREER_COLS = [
    ...BaseColumnDefinitions.PLAYER_BASE_COLS,
    BaseColumnDefinitions.TEAM_ABBREVS,
    BaseColumnDefinitions.GAMES_PLAYED_COL,
    ...BaseColumnDefinitions.GAP_COLUMNS,
    WIN_LOSS_TIE_RECORD_COL,
];
const ELIMINATION_GAME_SEASON_COLS = [
    ...BaseColumnDefinitions.PLAYER_BASE_COLS,
    BaseColumnDefinitions.TEAM_COL,
    BaseColumnDefinitions.SEASON_COL,
    BaseColumnDefinitions.GAMES_PLAYED_COL,
    ...BaseColumnDefinitions.GAP_COLUMNS,
    WIN_LOSS_TIE_RECORD_COL,
];
const ELIMINATION_GAME_SERIES_COLS = [
    ...BaseColumnDefinitions.PLAYER_BASE_COLS,
    BaseColumnDefinitions.TEAM_COL,
    { ...BaseColumnDefinitions.OPPONENT_COL, accessor: 'opponentName' },
    BaseColumnDefinitions.SEASON_COL,
    BaseColumnDefinitions.SERIES_TITLE,
    BaseColumnDefinitions.GAMES_PLAYED_COL,
    ...BaseColumnDefinitions.GAP_COLUMNS,
    WIN_LOSS_TIE_RECORD_COL,
];
const ELIMINATION_GAME_COLS = [
    ...BaseColumnDefinitions.PLAYER_BASE_COLS,
    BaseColumnDefinitions.TEAM_COL,
    BaseColumnDefinitions.GAME_SCORE_COL,
    BaseColumnDefinitions.GAME_DATE_COL,
    ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ...BaseColumnDefinitions.GAP_COLUMNS,
];
const PLAYOFF_MULTI_GOAL_COLS = [
    THREE_OR_MORE_GOAL_GAMES_COL,
    THREE_GOAL_GAMES_COL,
    FOUR_GOAL_GAMES_COL,
    FIVE_GOAL_GAMES_COL,
];

// Common primary sorts -- also listed alphabetically
const AVERAGES_COLUMNS = [
    ...BaseColumnDefinitions.GAP_COLUMNS,
    {
        accessor: 'goalsPerGame',
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => statFormatter.displayValueAs('goalsPerGame')(value),
        Header: CreateHeaderCell('Goals Per Game (min. 200 goals)', 'G/GP'),
        sortType: 'basic',
    },
    {
        accessor: 'assistsPerGame',
        Header: CreateHeaderCell('Assists Per Game (min. 300 assists)', 'A/GP'),
        Cell: ({ value }) => statFormatter.displayValueAs('assistsPerGame')(value),
        getProps: GlobalColumnFunctions.getProps,
        sortType: 'basic',
    },
    {
        accessor: 'pointsPerGame',
        Header: CreateHeaderCell('Points Per Game (min. 500 points)', 'P/GP'),
        Cell: ({ value }) => statFormatter.displayValueAs('pointsPerGame')(value),
        getProps: GlobalColumnFunctions.getProps,
        sortType: 'basic',
    },
];
const BASE_COLUMNS = [
    ...BaseColumnDefinitions.GAP_COLUMNS,
    BaseColumnDefinitions.POWER_PLAY_GOALS_COL,
    BaseColumnDefinitions.SHORTHAND_GOALS_COL,
];
const CAREER_BASE = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.PLAYER_COL,
    BaseColumnDefinitions.TEAM_ABBREVS,
];
const CONSECUTIVE_BASE_COLUMNS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.PLAYER_COL,
    BaseColumnDefinitions.SEASONS_PLAYED_COL,
    BaseColumnDefinitions.TEAM_ABBREVS,
];
const CONSECUTIVE_BASE_TIME_COLUMNS = [
    {
        accessor: 'streakStartSeason',
        Header: CreateHeaderCell('Start Season'),
        Cell: ({ value }) => (value ? hockeyFormatter.singleSeasonFormatter(value) : ''),
        getProps: GlobalColumnFunctions.getProps,
    },
    {
        accessor: 'streakEndSeason',
        Header: CreateHeaderCell('End Season'),
        Cell: ({ value }) => (value ? hockeyFormatter.singleSeasonFormatter(value) : ''),
        getProps: GlobalColumnFunctions.getProps,
    },
];
const FACEOFFS_BASE_COLUMNS = [
    BaseColumnDefinitions.FACEOFFS_TAKEN_COL,
    BaseColumnDefinitions.FACEOFFS_WON_COL,
    BaseColumnDefinitions.FACEOFFS_LOST_COL,
    BaseColumnDefinitions.FACEOFF_WIN_PCTG_COL,
];
const FASTEST_GOAL_BASE_COLUMNS = [
    ...BaseColumnDefinitions.PLAYER_SEASON_WITHOUT_GP_COLS,
    {
        accessor: 'gamesPlayedCareer',
        Header: CreateHeaderCell('Games Played Career', 'GP Career'),
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
    },
    BaseColumnDefinitions.GAME_DATE_COL,
    BaseColumnDefinitions.SCORE_COL,
];
const FASTEST_MULTI_GOAL_BASE_COLUMNS = [
    ...BaseColumnDefinitions.PLAYER_SEASON_WITHOUT_GP_COLS,
    BaseColumnDefinitions.GAME_DATE_COL,
    BaseColumnDefinitions.SCORE_COL,
];
const FIRST_AND_LAST_SEASONS = [
    {
        accessor: 'firstSeasonForGameType',
        getProps: GlobalColumnFunctions.getProps,
        Header: CreateHeaderCell('First Season'),
        Cell: ({ value }) => (value ? hockeyFormatter.singleSeasonFormatter(value) : ''),
    },
    {
        accessor: 'lastSeasonForGameType',
        getProps: GlobalColumnFunctions.getProps,
        Header: CreateHeaderCell('Last Season'),
        Cell: ({ value }) => (value ? hockeyFormatter.singleSeasonFormatter(value) : ''),
    },
];
const STREAK_BASE_COLUMNS = [
    ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
    ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
];
const MOST_X_OR_MORE_COLUMNS = [
    ...CONSECUTIVE_BASE_COLUMNS,
    {
        accessor: 'twentyGoalSeasons',
        Header: CreateHeaderCell('Twenty Goal Seasons'),
        getProps: GlobalColumnFunctions.getProps,
    },
    {
        accessor: 'thirtyGoalSeasons',
        Header: CreateHeaderCell('Thirty Goal Seasons'),
        getProps: GlobalColumnFunctions.getProps,
    },
    {
        accessor: 'fortyGoalSeasons',
        Header: CreateHeaderCell('Forty Goal Seasons'),
        getProps: GlobalColumnFunctions.getProps,
    },
    {
        accessor: 'fiftyGoalSeasons',
        Header: CreateHeaderCell('Fifty Goal Seasons'),
        getProps: GlobalColumnFunctions.getProps,
    },
    {
        accessor: 'sixtyGoalSeasons',
        Header: CreateHeaderCell('Sixty Goal Seasons'),
        getProps: GlobalColumnFunctions.getProps,
    },
    {
        accessor: 'oneHundredPointSeasons',
        Header: CreateHeaderCell('One Hundred Point Seasons'),
        getProps: GlobalColumnFunctions.getProps,
    },
];
const OVERTIME_GAP_COLS = [OVERTIME_GOALS_COL, OVERTIME_ASSISTS_COL, OVERTIME_POINTS_COL];
const GAME_SEVEN_COLUMNS = [
    GAME_SEVEN_GAMES_PLAYED_COL,
    GAME_SEVEN_GOALS_COL,
    GAME_SEVEN_ASSISTS_COL,
    GAME_SEVEN_POINTS_COL,
    GAME_SEVEN_RECORD,
];
const PENALTY_COLUMNS = [BaseColumnDefinitions.PENALTIES_COL, BaseColumnDefinitions.PENALTY_MINUTES_COL];
const PERIOD_BASE_COLUMNS = [
    ...BaseColumnDefinitions.PLAYER_SEASON_WITHOUT_GP_COLS,
    BaseColumnDefinitions.GAME_DATE_COL,
    BaseColumnDefinitions.SCORE_COL,
    BaseColumnDefinitions.PERIOD_COL,
];
const PP_AND_SH_COLUMNS = [BaseColumnDefinitions.POWER_PLAY_GOALS_COL, BaseColumnDefinitions.SHORTHAND_GOALS_COL];
const REALTIME_COLUMNS = [
    BaseColumnDefinitions.HITS_COL,
    BaseColumnDefinitions.BLOCKED_SHOTS_COL,
    BaseColumnDefinitions.MISSED_SHOTS_COL,
    BaseColumnDefinitions.GIVEAWAYS_COL,
    BaseColumnDefinitions.TAKEAWAYS_COL,
];
const REALTIME_ONE_GAME_BASE = [
    ...BaseColumnDefinitions.PLAYER_SEASON_WITHOUT_GP_COLS,
    BaseColumnDefinitions.SCORE_COL,
    BaseColumnDefinitions.GAME_DATE_COL,
];
const ROOKIE_BASE_COLUMNS = [
    ...BaseColumnDefinitions.PLAYER_SEASON_WITHOUT_GP_COLS,
    BaseColumnDefinitions.GAME_DATE_COL,
    BaseColumnDefinitions.SCORE_COL,
];
const SHOOTOUT_COLUMNS = [
    BaseColumnDefinitions.GOALS_COL,
    {
        accessor: 'gameDecidingGoals',
        Header: CreateHeaderCell('Game Deciding Goals', 'GDG'),
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
    },
    {
        accessor: 'shotsTaken',
        Header: CreateHeaderCell('Shots Taken', 'Shots'),
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
    },
    SHOOTING_PCTG_COL,
];
const TIME_ON_ICE_COLUMNS = [BaseColumnDefinitions.TIME_ON_ICE_COL, SHIFTS_COL];
const POINT_STREAK_ADDL_COLS = [
    BaseColumnDefinitions.GOALS_COL,
    BaseColumnDefinitions.ASSISTS_COL,
    BaseColumnDefinitions.POINTS_COL,
];
const GWG_COLS = [
    ...BASE_COLUMNS,
    {
        accessor: 'gameWinningGoals',
        getProps: GlobalColumnFunctions.getProps,
        Header: CreateHeaderCell('GWG'),
    },
];
const INCLUDING_PLAYOFFS_GAP = [
    {
        accessor: 'rsGamesPlayed',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Regular Season Games Played', 'RS GP'),
    },
    {
        accessor: 'rsGoals',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Regular Season Goals', 'RS G'),
    },
    {
        accessor: 'rsAssists',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Regular Season Assists', 'RS A'),
    },
    {
        accessor: 'rsPoints',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Regular Season Points', 'RS P'),
    },
    {
        accessor: 'poGamesPlayed',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Playoffs GamesPlayed', 'PO GP'),
    },
    {
        accessor: 'poGoals',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Playoffs Goals', 'PO G'),
    },
    {
        accessor: 'poAssists',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Playoffs Assists', 'PO A'),
    },
    {
        accessor: 'poPoints',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Playoffs Points', 'PO P'),
    },
    {
        accessor: 'gamesPlayed',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Games Played', 'GP'),
    },
    {
        accessor: 'goals',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Goals', 'G'),
    },
    {
        accessor: 'assists',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Assists', 'A'),
    },
    {
        accessor: 'points',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Points', 'P'),
    },
];
const INCLUDING_PLAYOFFS_GWG = [
    {
        accessor: 'rsGamesPlayed',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Regular Season Games Played', 'RS GP'),
    },
    {
        accessor: 'rsGoals',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Regular Season Goals', 'RS G'),
    },
    {
        accessor: 'rsGameWinningGoals',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Regular Season Game-Winning Goals', 'RS GWG'),
    },
    {
        accessor: 'poGamesPlayed',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Playoffs GamesPlayed', 'PO GP'),
    },
    {
        accessor: 'poGoals',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Playoffs Goals', 'PO G'),
    },
    {
        accessor: 'poGameWinningGoals',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Playoffs Game-Winning Goals', 'PO GWG'),
    },
    {
        accessor: 'gamesPlayed',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Total Games Played', 'T GP'),
    },
    {
        accessor: 'goals',
        getProps: GlobalColumnFunctions.getProps,
        minWidth: 40,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Total Goals', 'T G'),
    },
];
const SEASON_INCLUDING_PLAYOFFS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.PLAYER_COL,
    BaseColumnDefinitions.SEASON_COL,
    BaseColumnDefinitions.TEAMS_COL,
    ...INCLUDING_PLAYOFFS_GAP,
];
const SEASON_INCLUDING_PLAYOFFS_GWG = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.PLAYER_COL,
    BaseColumnDefinitions.TEAMS_COL,
    BaseColumnDefinitions.SEASON_COL,
    ...INCLUDING_PLAYOFFS_GWG,
];
/*
const CAREER_PPG_AND_SHG = [
    REGULAR_SEASON_GP,
    {
        accessor: 'rsPowerPlayGoals',
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Regular Season Power Play Goals', 'RS PPG')
    },
    {
        accessor: 'rsShorthandedGoals',
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Regular Season Shorthanded Goals', 'RS SHG')
    },
    PLAYOFFS_SEASON_GP,
    {
        accessor: 'poPowerPlayGoals',
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Playoffs Power Play Goals', 'PO PPG')
    },
    {
        accessor: 'poShorthandedGoals',
        getProps: GlobalColumnFunctions.getProps,
        Cell: ({ value }) => stringFormatter.cellDisplay(value),
        Header: CreateHeaderCell('Playoffs Shorthanded Goals', 'PO SHG')
    },
    BaseColumnDefinitions.GAMES_PLAYED_COL,
    POWER_PLAY_GOALS_COL,
    SHORTHAND_GOALS_COL
];
 */

const YOUNGEST_COLS = [
    BaseColumnDefinitions.GAME_COL,
    BaseColumnDefinitions.SCORE_COL,
    BaseColumnDefinitions.AGE_COLUMN,
    ...BaseColumnDefinitions.GAP_COLUMNS,
];

const REGULAR_SEASON_LATE_GOAL_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.PLAYER_COL,
    {
        ...BaseColumnDefinitions.TEAM_COL,
        accessor: 'team.fullName',
        sortType: (a, b) => a.original.team.fullName.toUpperCase().localeCompare(b.original.team.fullName.toUpperCase()),
    },
    BaseColumnDefinitions.SEASON_COL,
    {
        ...BaseColumnDefinitions.TEAM_COL,
        accessor: 'opponent.fullName',
        Header: CreateHeaderCell('Opponent', 'Opponent', { textAlign: 'left' }),
        sortType: (a, b) => a.original.opponent.fullName.toUpperCase().localeCompare(b.original.opponent.fullName.toUpperCase()),
    },
    BaseColumnDefinitions.GAME_DATE_COL,
    BaseColumnDefinitions.LATE_GOAL_SCORE_COL,
    {
        ...BaseColumnDefinitions.SECONDS_REMAINING_COL,
        Header: CreateHeaderCell('Seconds of Period', 'Seconds Remaining in 3rd'),
        accessor: 'secondsOfGame',
    },
];

const PLAYOFF_SEASON_LATE_GOAL_COLS = [
    BaseColumnDefinitions.INDEX_COL,
    BaseColumnDefinitions.PLAYER_COL,
    { ...BaseColumnDefinitions.TEAM_COL, accessor: 'team.fullName' },
    BaseColumnDefinitions.SEASON_COL,
    {
        ...BaseColumnDefinitions.TEAM_COL,
        accessor: 'opponent.fullName',
        Header: CreateHeaderCell('Opponent', 'Opponent', { textAlign: 'left' }),
    },
    {
        ...BaseColumnDefinitions.SERIES_TITLE,
        Cell: ({ original }) =>
            hockeyFormatter.isRoundRobinPlayoffGame(original.gameId) ? 'Round Robin' : original.seriesTitle,
    },
    BaseColumnDefinitions.GAME_DATE_COL,
    BaseColumnDefinitions.LATE_GOAL_SCORE_COL,
    {
        ...BaseColumnDefinitions.SECONDS_REMAINING_COL,
        Header: CreateHeaderCell('Seconds of Period', 'Seconds Remaining in 3rd'),
        accessor: 'secondsOfGame',
    },
];

export const skaterColumnDefinitions = {
    // Seasons
    'most-seasons': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        BaseColumnDefinitions.SEASONS_PLAYED_COL,
        ...FIRST_AND_LAST_SEASONS,
    ],

    // Seasons - playoffs
    'most-seasons-played-career-playoff': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        BaseColumnDefinitions.SEASONS_PLAYED_COL,
        ...BASE_COLUMNS,
        STANLEY_CUP_WINS,
    ],
    'skater-most-consecutive-seasons-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        ...BaseColumnDefinitions.START_END_SEASON_COLS,
        {
            accessor: 'consecutivePlayoffSeasons',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Consecutive Playoff Seasons', 'Consec. PS'),
        },
        BaseColumnDefinitions.PLAYOFF_SEASONS_COL,
        STANLEY_CUP_WINS,
    ],

    // Games
    'most-games-played': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...FIRST_AND_LAST_SEASONS],
    'most-games-played-career-including-playoffs': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        REGULAR_SEASON_GP,
        PLAYOFFS_SEASON_GP,
    ],
    'most-consecutive-games-played': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        {
            accessor: 'streak',
            Header: CreateHeaderCell('Streak'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],
    'most-consecutive-games-played-active-streak': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        BaseColumnDefinitions.TEAM_DISPLAY_COL,
        POSITION_CODE_COL,
        {
            accessor: 'streak',
            Header: CreateHeaderCell('Streak'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],

    // Games - playoffs
    'most-games-played-playoff': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-consecutive-games-played-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        {
            accessor: 'streak',
            Header: CreateHeaderCell('Streak'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
    ],

    // Goals
    'most-goals-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-goals-career-including-playoffs': [...CAREER_BASE, ...INCLUDING_PLAYOFFS_GAP],
    'skater-most-goals-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-goals-one-season-including-playoffs': SEASON_INCLUDING_PLAYOFFS,
    'most-goals-one-game': [...BaseColumnDefinitions.PLAYER_GAME_COLS, ...BASE_COLUMNS],
    'most-goals-one-road-game': [...BaseColumnDefinitions.PLAYER_GAME_COLS, ...BASE_COLUMNS],
    'most-goals-one-period': [...PERIOD_BASE_COLUMNS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-goals-50-games-from-season-start': [
        ...BaseColumnDefinitions.PLAYER_SEASON_WITHOUT_GP_COLS,
        {
            accessor: 'numberOfGamesInSeason',
            Header: CreateHeaderCell('Number of Games in Season', 'Games in Season'),
            getProps: GlobalColumnFunctions.getProps,
        },
        {
            accessor: 'goals',
            Header: CreateHeaderCell('50 Game Goals'),
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => stringFormatter.cellDisplay(value),
        },
        {
            accessor: 'assists',
            Header: CreateHeaderCell('50 Game Assists'),
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => stringFormatter.cellDisplay(value),
        },
        {
            accessor: 'points',
            Header: CreateHeaderCell('50 Game Points'),
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => stringFormatter.cellDisplay(value),
        },
        {
            accessor: 'seasonGoals',
            Header: CreateHeaderCell('Season Goals'),
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => stringFormatter.cellDisplay(value),
        },
        {
            accessor: 'seasonAssists',
            Header: CreateHeaderCell('Season Assists'),
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => stringFormatter.cellDisplay(value),
        },
        {
            accessor: 'seasonPoints',
            Header: CreateHeaderCell('Season Points'),
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => stringFormatter.cellDisplay(value),
        },
    ],

    // Goals - playoffs
    'most-goals-career-playoff': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-goals-one-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-goals-one-game-playoff': [
        ...BaseColumnDefinitions.PLAYER_GAME_COLS,
        ...BASE_COLUMNS,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'most-goals-one-period-playoff': [...PERIOD_BASE_COLUMNS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-goals-cup-final-playoff': [...BaseColumnDefinitions.SERIES_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-goals-one-series-playoff': [...BaseColumnDefinitions.SERIES_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],

    // Assists
    'most-assists-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-assists-career-including-playoffs': [...CAREER_BASE, ...INCLUDING_PLAYOFFS_GAP],
    'skater-most-assists-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-assists-one-season-including-playoffs': SEASON_INCLUDING_PLAYOFFS,
    'most-assists-one-game': [...BaseColumnDefinitions.PLAYER_GAME_COLS, ...BASE_COLUMNS],
    'most-assists-one-road-game': [...BaseColumnDefinitions.PLAYER_GAME_COLS, ...BASE_COLUMNS],
    'most-assists-one-period': [...PERIOD_BASE_COLUMNS, ...BaseColumnDefinitions.GAP_COLUMNS],

    // Assists - playoffs
    'most-assists-career-playoff': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-assists-one-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-assists-one-game-playoff': [
        ...BaseColumnDefinitions.PLAYER_GAME_COLS,
        ...BASE_COLUMNS,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'most-assists-one-period-playoff': [...PERIOD_BASE_COLUMNS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-assists-cup-final-playoff': [...BaseColumnDefinitions.SERIES_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-assists-one-series-playoff': [...BaseColumnDefinitions.SERIES_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],

    // Points
    'most-points-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-points-career-including-playoffs': [...CAREER_BASE, ...INCLUDING_PLAYOFFS_GAP],
    'skater-most-points-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-points-one-season-including-playoffs': SEASON_INCLUDING_PLAYOFFS,
    'most-points-one-game': [...BaseColumnDefinitions.PLAYER_GAME_COLS, ...BASE_COLUMNS],
    'most-points-one-road-game': [...BaseColumnDefinitions.PLAYER_GAME_COLS, ...BASE_COLUMNS],
    'most-points-one-period': [...PERIOD_BASE_COLUMNS, ...BaseColumnDefinitions.GAP_COLUMNS],

    // Points - playoffs
    'most-points-career-playoff': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-points-one-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-points-one-game-playoff': [
        ...BaseColumnDefinitions.PLAYER_GAME_COLS,
        ...BASE_COLUMNS,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'most-points-one-period-playoff': [...PERIOD_BASE_COLUMNS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-points-one-series-playoff': [...BaseColumnDefinitions.SERIES_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-points-cup-final-playoff': [...BaseColumnDefinitions.SERIES_COLS, ...BaseColumnDefinitions.GAP_COLUMNS],

    // Power-Play and Shorthanded Goals
    'most-power-play-goals-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        ...PP_AND_SH_COLUMNS,
    ],
    'skater-most-power-play-goals-one-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        ...PP_AND_SH_COLUMNS,
    ],
    'most-power-play-goals-one-game': [
        ...BaseColumnDefinitions.PLAYER_GAME_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        ...PP_AND_SH_COLUMNS,
    ],
    'most-short-handed-goals-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        ...PP_AND_SH_COLUMNS,
    ],
    'most-short-handed-goals-one-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        ...PP_AND_SH_COLUMNS,
    ],
    'most-short-handed-goals-one-game': [
        ...BaseColumnDefinitions.PLAYER_GAME_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        ...PP_AND_SH_COLUMNS,
    ],

    // Power-Play and Shorthanded Goals - playoffs
    'most-ppg-career-playoff': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...PP_AND_SH_COLUMNS],
    'most-ppg-one-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...PP_AND_SH_COLUMNS],
    'most-ppg-one-game-playoff': [
        ...BaseColumnDefinitions.PLAYER_GAME_COLS,
        ...PP_AND_SH_COLUMNS,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'most-ppg-one-period-playoff': [...PERIOD_BASE_COLUMNS, ...PP_AND_SH_COLUMNS],
    'most-ppg-one-series-playoff': [...BaseColumnDefinitions.SERIES_COLS, ...PP_AND_SH_COLUMNS],
    'most-shg-career-playoff': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...PP_AND_SH_COLUMNS],
    'most-shg-one-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...PP_AND_SH_COLUMNS],
    'most-shg-one-game-playoff': [
        ...BaseColumnDefinitions.PLAYER_GAME_COLS,
        ...PP_AND_SH_COLUMNS,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'most-shg-one-series-playoff': [...BaseColumnDefinitions.SERIES_COLS, ...PP_AND_SH_COLUMNS],
    'most-shg-one-period-playoff': [...PERIOD_BASE_COLUMNS, ...PP_AND_SH_COLUMNS],

    // Overtime Scoring
    'most-ot-goals-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...OVERTIME_GAP_COLS],
    'most-ot-assists-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...OVERTIME_GAP_COLS],
    'most-ot-points-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...OVERTIME_GAP_COLS],
    'most-ot-goals-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...OVERTIME_GAP_COLS],

    // Overtime Scoring - playoffs
    'most-overtime-goals-career-playoff': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        ...OVERTIME_GAP_COLS,
    ],
    'most-overtime-goals-one-season-playoff': [
        ...BaseColumnDefinitions.PLAYER_SEASON_PLAYOFF_COLS,
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        ...OVERTIME_GAP_COLS,
    ],
    'most-overtime-goals-one-series-playoff': [
        ...BaseColumnDefinitions.PLAYOFF_SERIES_COLS,
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        OVERTIME_GOALS_COL,
    ],
    'most-overtime-assists-career-playoff': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        ...OVERTIME_GAP_COLS,
    ],
    'most-overtime-assists-one-season-playoff': [
        ...BaseColumnDefinitions.PLAYER_SEASON_PLAYOFF_COLS,
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        ...OVERTIME_GAP_COLS,
    ],
    'most-overtime-assists-one-series-playoff': [
        ...BaseColumnDefinitions.PLAYOFF_SERIES_COLS,
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        OVERTIME_ASSISTS_COL,
    ],
    'most-overtime-points-career-playoff': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        ...OVERTIME_GAP_COLS,
    ],
    'most-overtime-points-one-season-playoff': [
        ...BaseColumnDefinitions.PLAYER_SEASON_PLAYOFF_COLS,
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        ...OVERTIME_GAP_COLS,
    ],
    'most-overtime-points-one-series-playoff': [
        ...BaseColumnDefinitions.PLAYOFF_SERIES_COLS,
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        OVERTIME_POINTS_COL,
    ],
    'most-overtime-games-played-career-playoff': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        ...BaseColumnDefinitions.OT_RECORD_COLS,
    ],
    'most-overtime-wins-career-playoff': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        ...BaseColumnDefinitions.OT_RECORD_COLS,
    ],

    // Game 7 Scoring
    'most-game-7-games-played-playoff-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        GAME_SEVEN_GAMES_PLAYED_COL,
        GAME_SEVEN_WINS_COL,
        GAME_SEVEN_LOSSES_COL,
        GAME_SEVEN_WIN_PERCENTAGE_COL,
    ],
    'most-game-7-wins-playoff-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        GAME_SEVEN_GAMES_PLAYED_COL,
        GAME_SEVEN_WINS_COL,
        GAME_SEVEN_LOSSES_COL,
        GAME_SEVEN_WIN_PERCENTAGE_COL,
    ],
    'most-game-7-losses-playoff-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        GAME_SEVEN_GAMES_PLAYED_COL,
        GAME_SEVEN_WINS_COL,
        GAME_SEVEN_LOSSES_COL,
        GAME_SEVEN_WIN_PERCENTAGE_COL,
    ],
    'most-game-7-goals-playoff-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS, ...GAME_SEVEN_COLUMNS],
    'most-game-7-goals-playoff-year': [...BaseColumnDefinitions.PLAYER_SEASON_PLAYOFF_COLS, ...GAME_SEVEN_COLUMNS],
    'most-game-7-goals-playoff-game': [
        ...BaseColumnDefinitions.PLAYOFF_SERIES_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'most-game-7-assists-playoff-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS, ...GAME_SEVEN_COLUMNS],
    'most-game-7-assists-playoff-year': [...BaseColumnDefinitions.PLAYER_SEASON_PLAYOFF_COLS, ...GAME_SEVEN_COLUMNS],
    'most-game-7-assists-playoff-game': [
        ...BaseColumnDefinitions.PLAYOFF_SERIES_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'most-game-7-points-playoff-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS, ...GAME_SEVEN_COLUMNS],
    'most-game-7-points-playoff-year': [...BaseColumnDefinitions.PLAYER_SEASON_PLAYOFF_COLS, ...GAME_SEVEN_COLUMNS],
    'most-game-7-points-playoff-game': [
        ...BaseColumnDefinitions.PLAYOFF_SERIES_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],

    // Shootout Goals
    'most-shootout-goals-career': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        ...SHOOTOUT_COLUMNS,
    ],
    'most-shootout-goals-one-season': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.TEAMS_COL,
        ...SHOOTOUT_COLUMNS,
    ],
    'most-shootout-shots-taken-career': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        ...SHOOTOUT_COLUMNS,
    ],
    'most-shootout-shot-attempts-season': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        ...SHOOTOUT_COLUMNS,
    ],
    'best-shootout-scoring-percentage-career': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        ...SHOOTOUT_COLUMNS,
    ],
    'best-shootout-scoring-percentage-one-season': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.TEAMS_COL,
        ...SHOOTOUT_COLUMNS,
    ],
    'most-game-deciding-shootout-goals-career': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        ...SHOOTOUT_COLUMNS,
    ],
    'most-game-deciding-shootout-goals-one-season': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.TEAMS_COL,
        ...SHOOTOUT_COLUMNS,
    ],

    // Shootout Goals - playoffs
    'most-game-winning-goals-career-playoff': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        GAME_WINNING_GOAL,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'most-game-winning-goals-one-season-playoff': [
        ...BaseColumnDefinitions.PLAYER_SEASON_COLS,
        GAME_WINNING_GOAL,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'most-game-winning-goals-one-series-playoff': [
        ...BaseColumnDefinitions.SERIES_COLS,
        GAME_WINNING_GOAL,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],

    // Pre-Game Scoring Averages
    'highest-goals-per-game-average-career-100-plus': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...AVERAGES_COLUMNS],
    'highest-goals-per-game-average-one-season-20-plus': [
        ...BaseColumnDefinitions.PLAYER_SEASON_COLS,
        BaseColumnDefinitions.GOALS_COL,
        {
            accessor: 'goalsPerGpMin20',
            Header: CreateHeaderCell('Goals per Game', 'G/GP'),
            Cell: ({ value }) => statFormatter.displayValueAs('goalsPerGpMin20')(value),
            getProps: GlobalColumnFunctions.getProps,
            sortType: 'basic',
        },
    ],
    'highest-goals-per-game-average-one-season-50-plus': [
        ...BaseColumnDefinitions.PLAYER_SEASON_COLS,
        BaseColumnDefinitions.GOALS_COL,
        {
            accessor: 'goalsPerGpMin50',
            Header: CreateHeaderCell('Goals Per Game Played (Minimum 50 Games)', 'G/GP'),
            Cell: ({ value }) => statFormatter.displayValueAs('goalsPerGpMin50')(value),
            getProps: GlobalColumnFunctions.getProps,
            sortType: 'basic',
        },
    ],
    'highest-assists-per-game-average-career-300-plus': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...AVERAGES_COLUMNS],
    'highest-assists-per-game-average-one-season-20-plus': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        {
            accessor: 'assistsPerGpMin20',
            Header: CreateHeaderCell('Assists Per Game Played (Minimum 20 Games)', 'A/GP'),
            Cell: ({ value }) => statFormatter.displayValueAs('assistsPerGame')(value),
            getProps: GlobalColumnFunctions.getProps,
            sortType: 'basic',
        },
    ],
    'highest-points-per-game-average-career-100-plus': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...AVERAGES_COLUMNS],
    'highest-points-per-game-average-one-season-50-plus': [
        ...BaseColumnDefinitions.PLAYER_SEASON_COLS,
        BaseColumnDefinitions.POINTS_COL,
        {
            accessor: 'pointsPerGpMin50',
            Header: CreateHeaderCell('Points Per Game Played (Minimum 50 Games)', 'P/GP'),
            Cell: ({ value }) => statFormatter.displayValueAs('pointsPerGpMin50')(value),
            getProps: GlobalColumnFunctions.getProps,
            sortType: 'basic',
        },
    ],

    // Scoring Plateaus
    'most-20-or-more-goal-season': MOST_X_OR_MORE_COLUMNS,
    'most-30-or-more-goal-season': MOST_X_OR_MORE_COLUMNS,
    'most-40-or-more-goal-season': MOST_X_OR_MORE_COLUMNS,
    'most-50-or-more-goal-season': MOST_X_OR_MORE_COLUMNS,
    'most-60-or-more-goal-season': MOST_X_OR_MORE_COLUMNS,
    'most-100-or-more-point-point-season': MOST_X_OR_MORE_COLUMNS,
    'most-consecutive-20-or-more-goal-season': [
        ...CONSECUTIVE_BASE_COLUMNS,
        {
            accessor: 'consecutive20GoalSeasons',
            Header: CreateHeaderCell('Consecutive 20 Goal Seasons'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...CONSECUTIVE_BASE_TIME_COLUMNS,
    ],
    'most-consecutive-30-or-more-goal-season': [
        ...CONSECUTIVE_BASE_COLUMNS,
        {
            accessor: 'consecutive30GoalSeasons',
            Header: CreateHeaderCell('Consecutive 30 Goal Seasons'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...CONSECUTIVE_BASE_TIME_COLUMNS,
    ],
    'most-consecutive-40-or-more-goal-season': [
        ...CONSECUTIVE_BASE_COLUMNS,
        {
            accessor: 'consecutive40GoalSeasons',
            Header: CreateHeaderCell('Consecutive 40 Goal Seasons'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...CONSECUTIVE_BASE_TIME_COLUMNS,
    ],
    'most-consecutive-50-or-more-goal-season': [
        ...CONSECUTIVE_BASE_COLUMNS,
        {
            accessor: 'consecutive50GoalSeasons',
            Header: CreateHeaderCell('Consecutive 50 Goal Seasons'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...CONSECUTIVE_BASE_TIME_COLUMNS,
    ],
    'most-consecutive-60-or-more-goal-season': [
        ...CONSECUTIVE_BASE_COLUMNS,
        {
            accessor: 'consecutive60GoalSeasons',
            Header: CreateHeaderCell('Consecutive 60 Goal Seasons'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...CONSECUTIVE_BASE_TIME_COLUMNS,
    ],
    'most-consecutive-100-or-more-point-season': [
        ...CONSECUTIVE_BASE_COLUMNS,
        {
            accessor: 'consecutive100PointSeasons',
            Header: CreateHeaderCell('Consecutive 100 Point Seasons'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...CONSECUTIVE_BASE_TIME_COLUMNS,
    ],

    // Three or More Goal Games
    'most-3-or-more-goal-game-one-season': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        THREE_OR_MORE_GOAL_GAMES_COL,
        THREE_GOAL_GAMES_COL,
        FOUR_GOAL_GAMES_COL,
        FIVE_GOAL_GAMES_COL,
        {
            accessor: 'sixGoalGames',
            Header: CreateHeaderCell('Six Goal Games'),
            getProps: GlobalColumnFunctions.getProps,
        },
        {
            accessor: 'sevenGoalGames',
            Header: CreateHeaderCell('Seven Goal Games'),
            getProps: GlobalColumnFunctions.getProps,
        },
    ],
    'most-3-or-more-goal-game-career': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        BaseColumnDefinitions.SEASONS_PLAYED_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        THREE_OR_MORE_GOAL_GAMES_COL,
    ],

    // Three or More Goal Games - playoff
    'most-3-or-more-goal-games-career-playoff': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...PLAYOFF_MULTI_GOAL_COLS],
    'most-3-or-more-goal-games-one-season-playoff': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        BaseColumnDefinitions.SEASON_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        {
            accessor: 'teamAbbrevs',
            Header: CreateHeaderCell('Team'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...PLAYOFF_MULTI_GOAL_COLS,
    ],
    'most-3-or-more-goal-games-one-series-playoff': [...BaseColumnDefinitions.SERIES_COLS, ...PLAYOFF_MULTI_GOAL_COLS],

    // Scoring Streaks - playoffs
    'longest-consecutive-goal-scoring-streak-one-season-playoff': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        POSITION_CODE_COL,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
        {
            accessor: 'goalStreak',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Point Streak', 'Streak'),
        },
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'longest-consecutive-assist-streak-one-season-playoff': [
        ...STREAK_BASE_COLUMNS,
        {
            accessor: 'assistStreak',
            Header: CreateHeaderCell('Assist Streak'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...POINT_STREAK_ADDL_COLS,
    ],
    'longest-consecutive-point-scoring-streak-one-season-playoff': [
        ...BaseColumnDefinitions.PLAYER_SEASON_STREAKS_COLS,
        POSITION_CODE_COL,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
        POINT_STREAK,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'longest-point-streak-multiple-seasons-playoff': [
        ...BaseColumnDefinitions.PLAYER_MULTI_SEASON_STREAKS_COLS,
        POSITION_CODE_COL,
        ...BaseColumnDefinitions.LONGEST_TIME_COLUMNS,
        POINT_STREAK,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],

    // Fastest Goals and Assists
    'fastest-goal-first-nhl-game': [
        ...FASTEST_GOAL_BASE_COLUMNS,
        BaseColumnDefinitions.PERIOD_COL,
        SECONDS_OF_PERIOD_COL,
    ],
    'fastest-goal-period-start': [
        ...FASTEST_GOAL_BASE_COLUMNS,
        BaseColumnDefinitions.PERIOD_COL,
        SECONDS_OF_PERIOD_COL,
    ],
    'fastest-goal-game-start': [...FASTEST_GOAL_BASE_COLUMNS, SECONDS_OF_PERIOD_COL],
    'fastest-2-goals-game-start-one-skater': [
        ...FASTEST_MULTI_GOAL_BASE_COLUMNS,
        BaseColumnDefinitions.GOAL_ONE_COL('period'),
        BaseColumnDefinitions.GOAL_TWO_COL('period'),
        {
            ...BaseColumnDefinitions.SINCE_START_OF_PERIOD_COL,
            Header: CreateHeaderCell('Seconds of Game', 'Seconds from Start'),
        },
        SECONDS_SINCE_LAST_PLAYER_GAME_GOAL_COL,
    ],
    'fastest-2-goals-one-skater': [
        ...FASTEST_MULTI_GOAL_BASE_COLUMNS,
        BaseColumnDefinitions.GOAL_ONE_COL(),
        BaseColumnDefinitions.GOAL_TWO_COL(),
        SECONDS_SINCE_LAST_PLAYER_GAME_GOAL_COL,
    ],
    'fastest-3-goals-one-skater': [
        ...FASTEST_MULTI_GOAL_BASE_COLUMNS,
        BaseColumnDefinitions.GOAL_ONE_COL(),
        BaseColumnDefinitions.GOAL_TWO_COL(),
        BaseColumnDefinitions.GOAL_THREE_COL(),
        SECONDS_FROM_LAST_2_GOALS_COL,
    ],
    'fastest-3-assists-one-skater': [
        ...FASTEST_MULTI_GOAL_BASE_COLUMNS,
        BaseColumnDefinitions.ASSIST_ONE_COL,
        BaseColumnDefinitions.ASSIST_TWO_COL,
        BaseColumnDefinitions.ASSIST_THREE_COL,
        {
            accessor: 'secondsSinceLast2Assists',
            Header: CreateHeaderCell('Seconds Since Last Two Assists', 'Seconds'),
            getProps: GlobalColumnFunctions.getProps,
        },
    ],

    // Fastest Goals and Assists - playoffs
    'fastest-goal-period-start-playoff': [
        ...FASTEST_GOAL_BASE_COLUMNS,
        BaseColumnDefinitions.PERIOD_COL,
        SECONDS_OF_PERIOD_COL,
    ],
    'fastest-goal-game-start-playoff': [
        ...FASTEST_GOAL_BASE_COLUMNS,
        SECONDS_OF_PERIOD_COL,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'fastest-2-goals-playoff': [
        ...FASTEST_MULTI_GOAL_BASE_COLUMNS,
        BaseColumnDefinitions.GOAL_ONE_COL(),
        BaseColumnDefinitions.GOAL_TWO_COL(),
        SECONDS_SINCE_LAST_PLAYER_GAME_GOAL_COL,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'fastest-2-goals-game-start-playoff': [
        ...FASTEST_MULTI_GOAL_BASE_COLUMNS,
        BaseColumnDefinitions.GOAL_ONE_COL('period'),
        BaseColumnDefinitions.GOAL_TWO_COL('period'),
        {
            ...BaseColumnDefinitions.SINCE_START_OF_PERIOD_COL,
            Header: CreateHeaderCell('Seconds of Game', 'Seconds'),
        },
        SECONDS_SINCE_LAST_PLAYER_GAME_GOAL_COL,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'fastest-2-goals-period-start-playoff': [
        ...FASTEST_MULTI_GOAL_BASE_COLUMNS,
        BaseColumnDefinitions.GOAL_ONE_COL('period'),
        BaseColumnDefinitions.GOAL_TWO_COL('period'),
        BaseColumnDefinitions.SINCE_START_OF_PERIOD_COL,
    ],
    'fastest-3-goals-one-skater-playoff': [
        ...FASTEST_MULTI_GOAL_BASE_COLUMNS,
        BaseColumnDefinitions.GOAL_ONE_COL(),
        BaseColumnDefinitions.GOAL_TWO_COL(),
        BaseColumnDefinitions.GOAL_THREE_COL(),
        SECONDS_FROM_LAST_2_GOALS_COL,
    ],

    // Shots On Goal
    'most-shots-one-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_COLS,
        BaseColumnDefinitions.SHOTS_COL,
        BaseColumnDefinitions.GOALS_COL,
    ],
    'most-shots-one-game': [
        ...BaseColumnDefinitions.PLAYER_GAME_COLS,
        BaseColumnDefinitions.SCORE_COL,
        BaseColumnDefinitions.SHOTS_COL,
        BaseColumnDefinitions.GOALS_COL,
    ],
    'most-shots-one-period': [...PERIOD_BASE_COLUMNS, BaseColumnDefinitions.SHOTS_COL, BaseColumnDefinitions.GOALS_COL],

    // Time on Ice
    'most-time-on-ice-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        BaseColumnDefinitions.SEASONS_PLAYED_COL,
        ...TIME_ON_ICE_COLUMNS,
    ],
    'most-time-on-ice-forward-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        BaseColumnDefinitions.SEASONS_PLAYED_COL,
        ...TIME_ON_ICE_COLUMNS,
    ],
    'most-time-on-ice-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...TIME_ON_ICE_COLUMNS],
    'most-time-on-ice-forward-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...TIME_ON_ICE_COLUMNS],
    'most-time-on-ice-one-game': [...REALTIME_ONE_GAME_BASE, BaseColumnDefinitions.TIME_ON_ICE_COL],
    'most-time-on-ice-forward-one-game': [...REALTIME_ONE_GAME_BASE, BaseColumnDefinitions.TIME_ON_ICE_COL],

    // Shifts
    'most-shifts-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        BaseColumnDefinitions.SEASONS_PLAYED_COL,
        ...TIME_ON_ICE_COLUMNS,
    ],
    'most-shifts-forward-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        BaseColumnDefinitions.SEASONS_PLAYED_COL,
        ...TIME_ON_ICE_COLUMNS,
    ],
    'most-shifts-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...TIME_ON_ICE_COLUMNS],
    'most-shifts-forward-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...TIME_ON_ICE_COLUMNS],
    'most-shifts-one-game': [...REALTIME_ONE_GAME_BASE, SHIFTS_COL],
    'most-shifts-forward-one-game': [...REALTIME_ONE_GAME_BASE, SHIFTS_COL],

    // Giveaways
    'most-giveaways-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...REALTIME_COLUMNS],
    'skater-most-giveaways-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...REALTIME_COLUMNS],
    'most-giveaways-one-game': [...REALTIME_ONE_GAME_BASE, BaseColumnDefinitions.GIVEAWAYS_COL],

    // Hits
    'most-hits-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...REALTIME_COLUMNS],
    'skater-most-hits-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...REALTIME_COLUMNS],
    'most-hits-one-game': [...REALTIME_ONE_GAME_BASE, BaseColumnDefinitions.HITS_COL],

    // Takeaways
    'most-takeaways-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...REALTIME_COLUMNS],
    'skater-most-takeaways-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...REALTIME_COLUMNS],
    'most-takeaways-one-game': [...REALTIME_ONE_GAME_BASE, BaseColumnDefinitions.TAKEAWAYS_COL],

    // Blocked Shots
    'most-blocked-shots-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...REALTIME_COLUMNS],
    'most-blocked-shots-forward-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...REALTIME_COLUMNS],
    'most-blocked-shots-forward-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...REALTIME_COLUMNS],
    'skater-most-blocked-shots-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...REALTIME_COLUMNS],
    'most-blocked-shots-one-game': [...REALTIME_ONE_GAME_BASE, BaseColumnDefinitions.BLOCKED_SHOTS_COL],
    'most-blocked-shots-forward-one-game': [...REALTIME_ONE_GAME_BASE, BaseColumnDefinitions.BLOCKED_SHOTS_COL],

    // Missed Shots
    'most-missed-shots-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...REALTIME_COLUMNS],
    'skater-most-missed-shots-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...REALTIME_COLUMNS],
    'most-missed-shots-one-game': [...REALTIME_ONE_GAME_BASE, BaseColumnDefinitions.MISSED_SHOTS_COL],

    // Faceoffs
    'most-faceoffs-taken-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...FACEOFFS_BASE_COLUMNS],
    'most-faceoffs-won-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...FACEOFFS_BASE_COLUMNS],
    'highest-faceoff-win-pctg-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...FACEOFFS_BASE_COLUMNS],
    'highest-faceoff-win-pctg-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...FACEOFFS_BASE_COLUMNS],
    'most-faceoffs-taken-one-game': [...REALTIME_ONE_GAME_BASE, ...FACEOFFS_BASE_COLUMNS],
    'most-faceoffs-won-one-game': [...REALTIME_ONE_GAME_BASE, ...FACEOFFS_BASE_COLUMNS],
    'skater-highest-faceoff-win-pctg-one-game': [...REALTIME_ONE_GAME_BASE, ...FACEOFFS_BASE_COLUMNS],
    'most-faceoffs-won-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...FACEOFFS_BASE_COLUMNS],
    'most-faceoffs-taken-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...FACEOFFS_BASE_COLUMNS],

    // Penalties
    'most-penalty-minutes-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...PENALTY_COLUMNS],
    'most-penalty-minutes-career-including-playoffs': [
        ...CAREER_BASE,
        REGULAR_SEASON_GP,
        {
            accessor: 'rsPenalties',
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => stringFormatter.cellDisplay(value),
            Header: CreateHeaderCell('Regular Season Penalties', 'RS Pen'),
        },
        {
            accessor: 'rsPenaltyMinutes',
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => stringFormatter.cellDisplay(value),
            Header: CreateHeaderCell('Regular Season Penalty Minutes', 'RS PIM'),
        },
        PLAYOFFS_SEASON_GP,
        {
            accessor: 'poPenalties',
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => stringFormatter.cellDisplay(value),
            Header: CreateHeaderCell('Playoffs Penalties', 'PO Pen'),
        },
        {
            accessor: 'poPenaltyMinutes',
            getProps: GlobalColumnFunctions.getProps,
            Cell: ({ value }) => stringFormatter.cellDisplay(value),
            Header: CreateHeaderCell('Playoffs Penalty Minutes', 'PO PIM'),
        },
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        ...PENALTY_COLUMNS,
    ],
    'skater-most-penalty-minutes-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...PENALTY_COLUMNS],
    'most-penalties-one-game': [...BaseColumnDefinitions.PLAYER_GAME_COLS, ...PENALTY_COLUMNS],
    'most-penalty-minutes-one-game': [...BaseColumnDefinitions.PLAYER_GAME_COLS, ...PENALTY_COLUMNS],
    'most-penalties-one-period': [...PERIOD_BASE_COLUMNS, ...PENALTY_COLUMNS],
    'most-penalty-minutes-one-period': [...PERIOD_BASE_COLUMNS, ...PENALTY_COLUMNS],

    // Penalties - playoff
    'most-penalty-minutes-career-playoff': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME,
        ...PENALTY_COLUMNS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'most-penalty-minutes-one-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...PENALTY_COLUMNS],
    'most-penalties-one-game-playoff': [
        ...BaseColumnDefinitions.PLAYER_GAME_COLS,
        ...PENALTY_COLUMNS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'most-penalty-minutes-one-game-playoff': [
        ...BaseColumnDefinitions.PLAYER_GAME_COLS,
        ...PENALTY_COLUMNS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'most-penalties-one-period-playoff': [...PERIOD_BASE_COLUMNS, ...PENALTY_COLUMNS],
    'most-penalty-minutes-one-period-playoff': [...PERIOD_BASE_COLUMNS, ...PENALTY_COLUMNS],

    // Consecutive
    'longest-consecutive-point-streak': [...STREAK_BASE_COLUMNS, POINT_STREAK, ...POINT_STREAK_ADDL_COLS],
    'longest-consecutive-point-streak-defenseman': [
        ...STREAK_BASE_COLUMNS,
        POINT_STREAK,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'longest-consecutive-point-streak-season-start': [...STREAK_BASE_COLUMNS, POINT_STREAK],
    'longest-consecutive-point-streak-start-of-career': [
        ...STREAK_BASE_COLUMNS,
        POINT_STREAK,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'longest-consecutive-point-streak-start-of-career-defenseman': [
        ...STREAK_BASE_COLUMNS,
        POINT_STREAK,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'longest-consecutive-point-streak-rookie': [...STREAK_BASE_COLUMNS, POINT_STREAK, ...POINT_STREAK_ADDL_COLS],
    'longest-consecutive-goal-streak': [
        ...STREAK_BASE_COLUMNS,
        {
            accessor: 'goalStreak',
            Header: CreateHeaderCell('Goal Streak'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...POINT_STREAK_ADDL_COLS,
    ],
    'longest-consecutive-goal-streak-defenseman': [
        ...STREAK_BASE_COLUMNS,
        {
            accessor: 'goalStreak',
            Header: CreateHeaderCell('Goal Streak'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...POINT_STREAK_ADDL_COLS,
    ],
    'longest-consecutive-goal-streak-rookie': [
        ...STREAK_BASE_COLUMNS,
        {
            accessor: 'goalStreak',
            Header: CreateHeaderCell('Goal Streak'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...POINT_STREAK_ADDL_COLS,
    ],
    'longest-consecutive-goal-streak-start-of-season': [
        ...STREAK_BASE_COLUMNS,
        {
            accessor: 'goalStreak',
            Header: CreateHeaderCell('Goal Streak'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...POINT_STREAK_ADDL_COLS,
    ],
    'longest-consecutive-goal-streak-start-of-season-defenseman': [
        ...STREAK_BASE_COLUMNS,
        {
            accessor: 'goalStreak',
            Header: CreateHeaderCell('Goal Streak'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...POINT_STREAK_ADDL_COLS,
    ],
    'longest-consecutive-goal-streak-start-of-season-rookie': [
        ...STREAK_BASE_COLUMNS,
        {
            accessor: 'goalStreak',
            Header: CreateHeaderCell('Goal Streak'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...POINT_STREAK_ADDL_COLS,
    ],
    'longest-consecutive-assist-streak': [
        ...STREAK_BASE_COLUMNS,
        {
            accessor: 'assistStreak',
            Header: CreateHeaderCell('Assist Streak'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...POINT_STREAK_ADDL_COLS,
    ],
    'longest-consecutive-assist-streak-defenseman': [
        ...STREAK_BASE_COLUMNS,
        {
            accessor: 'assistStreak',
            Header: CreateHeaderCell('Assist Streak'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...POINT_STREAK_ADDL_COLS,
    ],
    'longest-consecutive-assist-streak-rookie': [
        ...STREAK_BASE_COLUMNS,
        {
            accessor: 'assistStreak',
            Header: CreateHeaderCell('Assist Streak'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...POINT_STREAK_ADDL_COLS,
    ],
    'longest-consecutive-assist-streak-start-of-season': [
        ...STREAK_BASE_COLUMNS,
        {
            accessor: 'assistStreak',
            Header: CreateHeaderCell('Assist Streak'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...POINT_STREAK_ADDL_COLS,
    ],
    'longest-consecutive-assist-streak-start-of-season-defenseman': [
        ...STREAK_BASE_COLUMNS,
        {
            accessor: 'assistStreak',
            Header: CreateHeaderCell('Assist Streak'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...POINT_STREAK_ADDL_COLS,
    ],
    'longest-consecutive-assist-streak-start-of-season-rookie': [
        ...STREAK_BASE_COLUMNS,
        {
            accessor: 'assistStreak',
            Header: CreateHeaderCell('Assist Streak'),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...POINT_STREAK_ADDL_COLS,
    ],

    // Scoring By a Center
    'most-goals-center-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-goals-center-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-assists-center-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-assists-center-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-points-center-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-points-center-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],

    // Scoring By a Left Wing
    'most-goals-left-wing-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-goals-left-wing-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-assists-left-wing-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-assists-left-wing-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-points-left-wing-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-points-left-wing-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],

    // Scoring By a Right Wing
    'most-goals-right-wing-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-goals-right-wing-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-assists-right-wing-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-assists-right-wing-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-points-right-wing-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-points-right-wing-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],

    // Scoring By a Defenseman
    'most-goals-defenseman-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-goals-defenseman-career-playoff': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-goals-defenseman-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-goals-defenseman-one-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-goals-defenseman-one-game': [...BaseColumnDefinitions.PLAYER_GAME_COLS, ...BASE_COLUMNS],
    'most-goals-defenseman-one-game-playoff': [
        ...BaseColumnDefinitions.PLAYER_GAME_COLS,
        ...BASE_COLUMNS,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'most-assists-defenseman-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-assists-defenseman-career-playoff': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-assists-defenseman-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-assists-defenseman-one-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-assists-defenseman-one-game': [...BaseColumnDefinitions.PLAYER_GAME_COLS, ...BASE_COLUMNS],
    'most-assists-defenseman-one-game-playoff': [
        ...BaseColumnDefinitions.PLAYER_GAME_COLS,
        ...BASE_COLUMNS,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],
    'most-points-defenseman-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-points-defenseman-career-playoff': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...BASE_COLUMNS],
    'most-points-defenseman-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-points-defenseman-one-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-points-defenseman-one-game': [...BaseColumnDefinitions.PLAYER_GAME_COLS, ...BASE_COLUMNS],
    'most-points-defenseman-one-game-playoff': [
        ...BaseColumnDefinitions.PLAYER_GAME_COLS,
        ...BASE_COLUMNS,
        ...BaseColumnDefinitions.SERIES_GAME_COLS,
    ],

    // ROOKIE
    // Goals
    'most-goals-rookie-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, BaseColumnDefinitions.GOALS_COL],
    'most-goals-rookie-first-season-one-game': [...ROOKIE_BASE_COLUMNS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-goals-rookie-defenseman-one-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_COLS,
        BaseColumnDefinitions.GOALS_COL,
    ],
    'most-goals-rookie-first-game': [...ROOKIE_BASE_COLUMNS, ...BaseColumnDefinitions.GAP_COLUMNS],

    // Assists
    'most-assists-rookie-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, BaseColumnDefinitions.ASSISTS_COL],
    'most-assists-rookie-first-season-one-game': [...ROOKIE_BASE_COLUMNS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-assists-rookie-defenseman-one-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_COLS,
        BaseColumnDefinitions.ASSISTS_COL,
    ],
    'most-assists-rookie-first-game': [...ROOKIE_BASE_COLUMNS, ...BaseColumnDefinitions.GAP_COLUMNS],

    // Points
    'most-points-rookie-one-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'most-points-rookie-first-season-one-game': [...ROOKIE_BASE_COLUMNS, ...BaseColumnDefinitions.GAP_COLUMNS],
    'most-points-rookie-defenseman-one-season': [
        ...BaseColumnDefinitions.PLAYER_SEASON_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'most-points-rookie-first-game': [...ROOKIE_BASE_COLUMNS, ...BaseColumnDefinitions.GAP_COLUMNS],

    // ROOKIE - playoffs
    'most-goals-rookie-one-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-assists-rookie-one-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],
    'most-points-rookie-one-season-playoff': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...BASE_COLUMNS],

    //Game Winning Goals
    'most-game-winning-goals-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...GWG_COLS],
    'most-game-winning-goals-career-including-playoffs': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_WITHOUT_GP_COLS,
        ...INCLUDING_PLAYOFFS_GWG,
        {
            accessor: 'gameWinningGoals',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Total Game-Winning Goals', 'T GWG'),
        },
    ],
    'most-game-winning-goals-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...GWG_COLS],
    'most-game-winning-goals-one-season-including-playoffs': [
        ...SEASON_INCLUDING_PLAYOFFS_GWG,
        {
            accessor: 'gameWinningGoals',
            getProps: GlobalColumnFunctions.getProps,
            Header: CreateHeaderCell('Total Game-Winning Goals', 'T GWG'),
        },
    ],
    'most-game-winning-goals-rookie': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...GWG_COLS],
    'most-game-winning-goals-defenseman-career': [...BaseColumnDefinitions.PLAYER_ALL_TIME, ...GWG_COLS],
    'most-game-winning-goals-defenseman-one-season': [...BaseColumnDefinitions.PLAYER_SEASON_COLS, ...GWG_COLS],

    //Youngest Player
    'youngest-players-first-nhl-goal': [
        ...BaseColumnDefinitions.YOUNGEST_BASE_COLS,
        {
            Header: CreateHeaderCell('Position', 'Pos'),
            accessor: 'positionCode',
            Cell: ({ original }) => (original.positionCode ? original.positionCode : original.position),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...YOUNGEST_COLS,
    ],
    'youngest-players-first-3-point-game': [
        ...BaseColumnDefinitions.YOUNGEST_BASE_COLS,
        {
            Header: CreateHeaderCell('Position', 'Pos'),
            accessor: 'positionCode',
            Cell: ({ original }) => (original.positionCode ? original.positionCode : original.position),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...YOUNGEST_COLS,
    ],
    'youngest-players-first-4-point-game': [
        ...BaseColumnDefinitions.YOUNGEST_BASE_COLS,
        {
            Header: CreateHeaderCell('Position', 'Pos'),
            accessor: 'positionCode',
            Cell: ({ original }) => (original.positionCode ? original.positionCode : original.position),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...YOUNGEST_COLS,
    ],
    'youngest-players-first-5-point-game': [
        ...BaseColumnDefinitions.YOUNGEST_BASE_COLS,
        {
            Header: CreateHeaderCell('Position', 'Pos'),
            accessor: 'positionCode',
            Cell: ({ original }) => (original.positionCode ? original.positionCode : original.position),
            getProps: GlobalColumnFunctions.getProps,
        },
        ...YOUNGEST_COLS,
    ],
    'youngest-defensemen-first-nhl-goal': [...BaseColumnDefinitions.YOUNGEST_BASE_COLS, ...YOUNGEST_COLS],
    'youngest-defensemen-first-3-point-game': [...BaseColumnDefinitions.YOUNGEST_BASE_COLS, ...YOUNGEST_COLS],
    'youngest-defensemen-first-4-point-game': [...BaseColumnDefinitions.YOUNGEST_BASE_COLS, ...YOUNGEST_COLS],
    'youngest-defensemen-first-5-point-game': [...BaseColumnDefinitions.YOUNGEST_BASE_COLS, ...YOUNGEST_COLS],
    // Stanley Cup Wins
    'skater-most-stanley-cups-won-career': [
        ...BaseColumnDefinitions.PLAYER_BASE_COLS,
        ...BaseColumnDefinitions.PLAYER_STANLEY_CUPS_COLS,
    ],
    'skater-most-consecutive-stanley-cups-won': [
        ...BaseColumnDefinitions.PLAYER_BASE_COLS,
        ...BaseColumnDefinitions.PLAYER_STANLEY_CUPS_COLS,
    ],

    // Outdoor Columns
    'outdoor-games-skater-registry': [
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.FIRST_OUTDOOR_SEASON_COL,
        BaseColumnDefinitions.LAST_OUTDOOR_SEASON_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.POINTS_COL,
    ],
    'most-outdoor-games-played-career-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.POINTS_COL,
    ],
    'most-outdoor-wins-career-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.WINS_COL,
        BaseColumnDefinitions.LOSSES_COL,
        BaseColumnDefinitions.OVERTIME_LOSSES_COL,
    ],
    'most-outdoor-goals-career-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.POINTS_COL,
    ],
    'most-outdoor-goals-game-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.POINTS_COL,
    ],
    'most-outdoor-goals-period-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.POINTS_COL,
    ],
    'most-outdoor-game-winning-goals-career-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        GAME_WINNING_GOAL,
    ],
    'most-outdoor-assists-career-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.POINTS_COL,
    ],
    'most-outdoor-assists-game-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.POINTS_COL,
    ],
    'most-outdoor-assists-period-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.POINTS_COL,
    ],
    'most-outdoor-points-career-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.POINTS_COL,
    ],
    'most-outdoor-points-game-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.POINTS_COL,
    ],
    'most-outdoor-points-period-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.POINTS_COL,
    ],
    'most-outdoor-power-play-goals-career-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.POWER_PLAY_GOALS_COL,
    ],
    'most-outdoor-power-play-goals-game-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.POWER_PLAY_GOALS_COL,
    ],
    'most-outdoor-shorthanded-goals-career-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.SHORTHAND_GOALS_COL,
    ],
    'most-outdoor-shorthanded-goals-game-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.SHORTHAND_GOALS_COL,
    ],
    'most-outdoor-goals-by-a-rookie-game-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.POINTS_COL,
    ],
    'most-outdoor-assists-rookie-game-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.POINTS_COL,
    ],
    'most-outdoor-points-rookie-game-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.GOALS_COL,
        BaseColumnDefinitions.ASSISTS_COL,
        BaseColumnDefinitions.POINTS_COL,
    ],
    'fastest-goals-from-start-of-outdoor-game-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.SINCE_START_OF_PERIOD_COL,
    ],
    'fastest-goals-from-start-of-any-outdoor-period-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.SINCE_START_OF_PERIOD_COL,
    ],
    'most-outdoor-shots-on-goal-career-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.SHOTS_COL,
        BaseColumnDefinitions.GOALS_COL,
        SHOOTING_PCTG_COL,
    ],
    'most-outdoor-shots-on-goal-game-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.SHOTS_COL,
        BaseColumnDefinitions.GOALS_COL,
        SHOOTING_PCTG_COL,
    ],
    'most-outdoor-shots-on-goal-period-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.PERIOD_COL,
        BaseColumnDefinitions.SHOTS_COL,
        BaseColumnDefinitions.GOALS_COL,
        SHOOTING_PCTG_COL,
    ],
    'most-outdoor-faceoffs-taken-career-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.FACEOFFS_TAKEN_COL,
        BaseColumnDefinitions.FACEOFFS_WON_COL,
        BaseColumnDefinitions.FACEOFFS_LOST_COL,
        BaseColumnDefinitions.FACEOFF_WIN_PCTG_COL,
    ],
    'most-outdoor-faceoffs-taken-game-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.FACEOFFS_TAKEN_COL,
        BaseColumnDefinitions.FACEOFFS_WON_COL,
        BaseColumnDefinitions.FACEOFFS_LOST_COL,
        BaseColumnDefinitions.FACEOFF_WIN_PCTG_COL,
    ],
    'most-outdoor-faceoffs-won-career-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.FACEOFFS_TAKEN_COL,
        BaseColumnDefinitions.FACEOFFS_WON_COL,
        BaseColumnDefinitions.FACEOFFS_LOST_COL,
        BaseColumnDefinitions.FACEOFF_WIN_PCTG_COL,
    ],
    'most-outdoor-faceoffs-won-game-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.FACEOFFS_TAKEN_COL,
        BaseColumnDefinitions.FACEOFFS_WON_COL,
        BaseColumnDefinitions.FACEOFFS_LOST_COL,
        BaseColumnDefinitions.FACEOFF_WIN_PCTG_COL,
    ],
    'highest-outdoor-plus-minus-career-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        PLUS_MINUS_COL,
    ],
    'highest-outdoor-plus-minus-game-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        PLUS_MINUS_COL,
    ],
    'most-outdoor-hits-career-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.HITS_COL,
    ],
    'most-outdoor-hits-game-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.HITS_COL,
    ],
    'most-outdoor-blocked-shots-career-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.BLOCKED_SHOTS_COL,
    ],
    'most-outdoor-blocked-shots-game-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.BLOCKED_SHOTS_COL,
    ],
    'most-outdoor-time-on-ice-career-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_ABBREVS,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.TIME_ON_ICE_COL,
        BaseColumnDefinitions.TIME_ON_ICE_PER_GAME_COL,
    ],
    'most-outdoor-time-on-ice-game-skater': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        POSITION_CODE_COL,
        BaseColumnDefinitions.TEAM_COL,
        BaseColumnDefinitions.GAME_COL,
        BaseColumnDefinitions.EVENT_NAME_COL,
        BaseColumnDefinitions.TIME_ON_ICE_COL,
    ],
    'skater-most-goals-facing-elimination-career': ELIMINATION_GAME_CAREER_COLS,
    'skater-most-assists-facing-elimination-career': ELIMINATION_GAME_CAREER_COLS,
    'skater-most-points-facing-elimination-career': ELIMINATION_GAME_CAREER_COLS,
    'skater-most-games-played-facing-elimination-career': ELIMINATION_GAME_CAREER_COLS,
    'skater-most-goals-facing-elimination-season': ELIMINATION_GAME_SEASON_COLS,
    'skater-most-assists-facing-elimination-season': ELIMINATION_GAME_SEASON_COLS,
    'skater-most-points-facing-elimination-season': ELIMINATION_GAME_SEASON_COLS,
    'skater-most-games-played-facing-elimination-season': ELIMINATION_GAME_SEASON_COLS,
    'skater-most-goals-facing-elimination-series': ELIMINATION_GAME_SERIES_COLS,
    'skater-most-assists-facing-elimination-series': ELIMINATION_GAME_SERIES_COLS,
    'skater-most-points-facing-elimination-series': ELIMINATION_GAME_SERIES_COLS,
    'skater-most-goals-facing-elimination-game': ELIMINATION_GAME_COLS,
    'skater-most-assists-facing-elimination-game': ELIMINATION_GAME_COLS,
    'skater-most-points-facing-elimination-game': ELIMINATION_GAME_COLS,
    'skater-most-goals-clinching-games-career': ELIMINATION_GAME_CAREER_COLS,
    'skater-most-assists-clinching-games-career': ELIMINATION_GAME_CAREER_COLS,
    'skater-most-points-clinching-games-career': ELIMINATION_GAME_CAREER_COLS,
    'skater-most-games-played-clinching-games-career': ELIMINATION_GAME_CAREER_COLS,
    'skater-most-goals-clinching-games-season': ELIMINATION_GAME_SEASON_COLS,
    'skater-most-assists-clinching-games-season': ELIMINATION_GAME_SEASON_COLS,
    'skater-most-points-clinching-games-season': ELIMINATION_GAME_SEASON_COLS,
    'skater-most-games-played-clinching-games-season': ELIMINATION_GAME_SEASON_COLS,
    'skater-most-goals-clinching-games-series': ELIMINATION_GAME_SERIES_COLS,
    'skater-most-assists-clinching-games-series': ELIMINATION_GAME_SERIES_COLS,
    'skater-most-points-clinching-games-series': ELIMINATION_GAME_SERIES_COLS,
    'skater-most-goals-clinching-game': ELIMINATION_GAME_COLS,
    'skater-most-assists-clinching-game': ELIMINATION_GAME_COLS,
    'skater-most-points-clinching-game': ELIMINATION_GAME_COLS,
    'scf-skater-most-stanley-cups-won-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        { ...BaseColumnDefinitions.CUPS_WON_COL, Header: 'Stanley Cups' },
        BaseColumnDefinitions.SEASONS_WON_COL,
    ],
    'scf-skater-most-consecutive-stanley-cups-won': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        { ...BaseColumnDefinitions.CUPS_WON_COL, Header: 'Stanley Cups' },
        { ...BaseColumnDefinitions.LONGEST_STREAK_COL, Header: 'Consecutive Stanley Cups' },
        { ...BaseColumnDefinitions.STREAK_DESC_COL, Header: 'Streak Seasons' },
    ],
    'skater-most-stanley-cup-final-appearances-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        BaseColumnDefinitions.APPEARANCES_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.STANLEY_CUPS_COL,
    ],
    'skater-most-consecutive-stanley-cup-final-appearances': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        { ...BaseColumnDefinitions.APPEARANCES_COL, Header: 'Total Appearances' },
        BaseColumnDefinitions.CONSEC_SEASONS_COL,
        BaseColumnDefinitions.STREAK_SEASONS_COL,
    ],
    'skater-most-stanley-cup-final-games-played-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        BaseColumnDefinitions.APPEARANCES_COL,
        BaseColumnDefinitions.GAMES_PLAYED_COL,
        BaseColumnDefinitions.STANLEY_CUPS_COL,
    ],
    'skater-most-goals-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_MULTI_ALL_TIME,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'skater-most-goals-stanley-cup-final-series': [
        ...BaseColumnDefinitions.SCF_SERIES_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'skater-most-goals-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'skater-most-goals-stanley-cup-final-period': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'skater-most-assists-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_MULTI_ALL_TIME,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'skater-most-assists-stanley-cup-final-series': [
        ...BaseColumnDefinitions.SCF_SERIES_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'skater-most-assists-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'skater-most-assists-stanley-cup-final-period': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'skater-most-points-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_MULTI_ALL_TIME,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'skater-most-points-stanley-cup-final-series': [
        ...BaseColumnDefinitions.SCF_SERIES_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'skater-most-points-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'skater-most-points-stanley-cup-final-period': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'skater-most-power-play-goals-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_MULTI_ALL_TIME,
        ...BaseColumnDefinitions.PP_GAP_COLUMNS,
    ],
    'skater-most-power-play-goals-stanley-cup-final-series': [
        ...BaseColumnDefinitions.SCF_SERIES_COLS,
        ...BaseColumnDefinitions.PP_GAP_COLUMNS,
    ],
    'skater-most-power-play-goals-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        ...BaseColumnDefinitions.PP_GAP_COLUMNS,
    ],
    'skater-most-power-play-goals-stanley-cup-final-period': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        ...BaseColumnDefinitions.PP_GAP_COLUMNS,
    ],
    'skater-most-power-play-assists-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_MULTI_ALL_TIME,
        ...BaseColumnDefinitions.PP_GAP_COLUMNS,
    ],
    'skater-most-power-play-assists-stanley-cup-final-series': [
        ...BaseColumnDefinitions.SCF_SERIES_COLS,
        ...BaseColumnDefinitions.PP_GAP_COLUMNS,
    ],
    'skater-most-power-play-assists-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        ...BaseColumnDefinitions.PP_GAP_COLUMNS,
    ],
    'skater-most-power-play-assists-stanley-cup-final-period': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        ...BaseColumnDefinitions.PP_GAP_COLUMNS,
    ],
    'skater-most-power-play-points-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_MULTI_ALL_TIME,
        ...BaseColumnDefinitions.PP_GAP_COLUMNS,
    ],
    'skater-most-power-play-points-stanley-cup-final-series': [
        ...BaseColumnDefinitions.SCF_SERIES_COLS,
        ...BaseColumnDefinitions.PP_GAP_COLUMNS,
    ],
    'skater-most-power-play-points-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        ...BaseColumnDefinitions.PP_GAP_COLUMNS,
    ],
    'skater-most-power-play-points-stanley-cup-final-period': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        ...BaseColumnDefinitions.PP_GAP_COLUMNS,
    ],
    'skater-most-shorthanded-goals-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_MULTI_ALL_TIME,
        ...BaseColumnDefinitions.SH_GAP_COLUMNS,
    ],
    'skater-most-shorthanded-goals-stanley-cup-final-series': [
        ...BaseColumnDefinitions.SCF_SERIES_COLS,
        ...BaseColumnDefinitions.SH_GAP_COLUMNS,
    ],
    'skater-most-shorthanded-goals-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        ...BaseColumnDefinitions.SH_GAP_COLUMNS,
    ],
    'skater-most-shorthanded-assists-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_MULTI_ALL_TIME,
        ...BaseColumnDefinitions.SH_GAP_COLUMNS,
    ],
    'skater-most-shorthanded-assists-stanley-cup-final-series': [
        ...BaseColumnDefinitions.SCF_SERIES_COLS,
        ...BaseColumnDefinitions.SH_GAP_COLUMNS,
    ],
    'skater-most-shorthanded-assists-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        ...BaseColumnDefinitions.SH_GAP_COLUMNS,
    ],
    'skater-most-shorthanded-points-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_MULTI_ALL_TIME,
        ...BaseColumnDefinitions.SH_GAP_COLUMNS,
    ],
    'skater-most-shorthanded-points-stanley-cup-final-series': [
        ...BaseColumnDefinitions.SCF_SERIES_COLS,
        ...BaseColumnDefinitions.SH_GAP_COLUMNS,
    ],
    'skater-most-shorthanded-points-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        ...BaseColumnDefinitions.SH_GAP_COLUMNS,
    ],
    'skater-most-game-winning-goals-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_MULTI_ALL_TIME,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        GAME_WINNING_GOAL,
    ],
    'skater-most-game-winning-goals-stanley-cup-final-series': [
        ...BaseColumnDefinitions.SCF_SERIES_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
        GAME_WINNING_GOAL,
    ],
    'skater-most-overtime-games-played-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        { ...BaseColumnDefinitions.GAMES_PLAYED_COL, Header: 'Total GP' },
        ...BaseColumnDefinitions.OT_RECORD_COLS,
    ],
    'skater-most-overtime-wins-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        { ...BaseColumnDefinitions.GAMES_PLAYED_COL, Header: 'Total GP' },
        ...BaseColumnDefinitions.OT_RECORD_COLS,
    ],
    'skater-most-overtime-goals-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        { ...BaseColumnDefinitions.GAMES_PLAYED_COL, Header: 'Total GP' },
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        ...OVERTIME_GAP_COLS,
    ],
    'skater-most-overtime-goals-stanley-cup-final-series': [
        ...BaseColumnDefinitions.SCF_SERIES_BASE_COLS,
        { ...BaseColumnDefinitions.GAMES_PLAYED_COL, Header: 'Total GP' },
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        ...OVERTIME_GAP_COLS,
    ],
    'skater-most-overtime-assists-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        { ...BaseColumnDefinitions.GAMES_PLAYED_COL, Header: 'Total GP' },
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        ...OVERTIME_GAP_COLS,
    ],
    'skater-most-overtime-assists-stanley-cup-final-series': [
        ...BaseColumnDefinitions.SCF_SERIES_BASE_COLS,
        { ...BaseColumnDefinitions.GAMES_PLAYED_COL, Header: 'Total GP' },
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        ...OVERTIME_GAP_COLS,
    ],
    'skater-most-overtime-points-stanley-cup-final-career': [
        ...BaseColumnDefinitions.PLAYER_ALL_TIME_PLAYOFFS,
        { ...BaseColumnDefinitions.GAMES_PLAYED_COL, Header: 'Total GP' },
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        ...OVERTIME_GAP_COLS,
    ],
    'skater-most-overtime-points-stanley-cup-final-series': [
        ...BaseColumnDefinitions.SCF_SERIES_BASE_COLS,
        { ...BaseColumnDefinitions.GAMES_PLAYED_COL, Header: 'Total GP' },
        BaseColumnDefinitions.OVERTIME_GAMES_PLAYED_COL,
        ...OVERTIME_GAP_COLS,
    ],
    'skater-most-hat-tricks-stanley-cup-final-career': [...BaseColumnDefinitions.PLAYER_MULTI_ALL_TIME, HAT_TRICKS_COL],
    'skater-most-hat-tricks-stanley-cup-final-series': [...BaseColumnDefinitions.SCF_SERIES_COLS, HAT_TRICKS_COL],
    'skater-fastest-goals-from-start-of-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        SECONDS_OF_PERIOD_COL,
    ],
    'skater-fastest-goals-from-start-of-stanley-cup-final-period': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        SECONDS_OF_PERIOD_COL,
    ],
    'skater-fastest-two-goals-from-start-of-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        SECONDS_FROM_PERIOD_START_COL,
    ],
    'skater-fastest-two-goals-from-start-of-stanley-cup-final-period': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        BaseColumnDefinitions.PERIOD_COL,
        SECONDS_FROM_PERIOD_START_COL,
    ],
    'skater-fastest-two-goals-any-time-of-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        SECONDS_SINCE_LAST_PLAYER_GAME_GOAL_COL,
        BaseColumnDefinitions.GOAL_ONE_COL(),
        BaseColumnDefinitions.GOAL_TWO_COL(),
    ],
    'skater-fastest-three-goals-any-time-of-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        SECONDS_FROM_LAST_2_GOALS_COL,
        BaseColumnDefinitions.GOAL_ONE_COL(),
        BaseColumnDefinitions.GOAL_TWO_COL(),
        BaseColumnDefinitions.GOAL_THREE_COL(),
    ],
    'most-goals-rookie-stanley-cup-final-series': [
        ...BaseColumnDefinitions.SCF_SERIES_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'most-goals-rookie-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'most-assists-rookie-stanley-cup-final-series': [
        ...BaseColumnDefinitions.SCF_SERIES_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'most-assists-rookie-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'most-points-rookie-stanley-cup-final-series': [
        ...BaseColumnDefinitions.SCF_SERIES_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],
    'most-points-rookie-stanley-cup-final-game': [
        ...BaseColumnDefinitions.SCF_SKATER_GAME_COLS,
        ...BaseColumnDefinitions.GAP_COLUMNS,
    ],

    //Late Goals
    'latest-game-tying-goals-regulation-nhl-history': REGULAR_SEASON_LATE_GOAL_COLS,
    'latest-go-ahead-goals-regulation-nhl-history': [
        ...REGULAR_SEASON_LATE_GOAL_COLS,
        BaseColumnDefinitions.GAME_WINNING_GOAL_COL,
    ],
    'latest-goals-regulation-period-nhl-history': REGULAR_SEASON_LATE_GOAL_COLS,
    'latest-game-tying-goals-regulation-playoff-game': PLAYOFF_SEASON_LATE_GOAL_COLS,
    'latest-go-ahead-goals-regulation-playoff-game': [
        ...PLAYOFF_SEASON_LATE_GOAL_COLS,
        BaseColumnDefinitions.GAME_WINNING_GOAL_COL,
    ],
    'latest-series-clinching-goals-regulation-playoff-game': [
        BaseColumnDefinitions.INDEX_COL,
        BaseColumnDefinitions.PLAYER_COL,
        { ...BaseColumnDefinitions.TEAM_COL, accessor: 'team.fullName' },
        PLAYER_AGE_COL,
        BaseColumnDefinitions.SEASON_COL,
        {
            ...BaseColumnDefinitions.TEAM_COL,
            accessor: 'opponent.fullName',
            Header: CreateHeaderCell('Opponent', 'Opponent', { textAlign: 'left' }),
        },
        BaseColumnDefinitions.SERIES_TITLE,
        BaseColumnDefinitions.SERIES_GAME_NUMBER_COL,
        BaseColumnDefinitions.GAME_DATE_COL,
        BaseColumnDefinitions.LATE_GOAL_SCORE_COL,
        BaseColumnDefinitions.PERIOD_COL,
        TIME_OF_GOAL_COL,
    ],
    ...skaterMultiPerformancesColumnDefinitions,
    ...skaterInternationalColumns,
};
